import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { User, Copy, ArrowLeft, Send, Sparkles, Users as UsersIcon, TrendingUp } from 'lucide-react';
import UsernameSetup from '@/components/UsernameSetup';

/**
 * Public profile page — anyone can view any user's profile.
 * URL: /u/username  OR  /u/CRYPTOX1 (works for both handles)
 * Shows: display name, username, referral code, member-since, trades stat, referrals stat.
 */
export default function Profile() {
  const { handle } = useParams();
  const { user: me } = useAuth();
  const [profile, setProfile] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setProfile(null); setNotFound(false);
    (async () => {
      try {
        const r = await api.get(`/users/profile/${encodeURIComponent(handle)}`);
        setProfile(r.data);
      } catch (_e) { setNotFound(true); }
    })();
  }, [handle]);

  const isMe = me && profile && (me.username === profile.username || me.referral_code === profile.referral_code);

  if (notFound) return (
    <div className="min-h-[70vh] max-w-2xl mx-auto p-8 text-center">
      <User className="w-16 h-16 mx-auto text-[#5E6673] mb-4" />
      <h1 className="text-2xl font-bold text-white mb-2">User not found</h1>
      <p className="text-[#848E9C] mb-6">No BITGONODE user found for <span className="font-mono text-white">@{handle}</span>.</p>
      <Link to="/" className="inline-flex items-center gap-1 text-[#FCD535] hover:underline"><ArrowLeft className="w-4 h-4" /> Back home</Link>
    </div>
  );

  if (!profile) return <div className="p-10 text-[#848E9C]">Loading profile…</div>;

  const copy = (v, label) => { navigator.clipboard.writeText(v); toast.success(`${label} copied`); };
  const initial = (profile.name?.[0] || profile.username?.[0] || 'A').toUpperCase();

  return (
    <div className="max-w-3xl mx-auto p-6" data-testid="profile-page">
      <Link to="/" className="inline-flex items-center gap-1 text-[#848E9C] hover:text-white text-xs mb-4"><ArrowLeft className="w-3 h-3" /> Back</Link>
      <div className="border border-[#2B3139] bg-[#181A20] rounded-sm p-6">
        <div className="flex items-start gap-5">
          {profile.picture ? (
            <img src={profile.picture} alt={profile.name} className="w-24 h-24 rounded-full object-cover border-2 border-[#FCD535]" />
          ) : (
            <div className="w-24 h-24 rounded-full grid place-items-center text-4xl font-bold text-[#0B0E11] bg-gradient-to-br from-[#FCD535] to-[#F0B90B]">{initial}</div>
          )}
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-white text-3xl font-bold tracking-tighter" data-testid="profile-name">{profile.name}</h1>
            {profile.username && (
              <div className="text-[#FCD535] font-mono text-sm mt-0.5" data-testid="profile-username">@{profile.username}</div>
            )}
            <div className="flex items-center gap-2 mt-2 text-[10px] font-mono text-[#848E9C]">
              <span>REF:</span>
              <button onClick={() => copy(profile.referral_code, 'Referral code')}
                className="text-white hover:text-[#FCD535] inline-flex items-center gap-1" data-testid="profile-ref-copy">
                {profile.referral_code} <Copy className="w-2.5 h-2.5" />
              </button>
              <span className="text-[#5E6673]">·</span>
              <span>Member since {new Date(profile.member_since).toLocaleDateString(undefined, { year: 'numeric', month: 'long' })}</span>
            </div>
            {!isMe && me && (
              <div className="flex gap-2 mt-4">
                <Link to={`/wallet?send=${profile.username || profile.referral_code}`}
                  className="inline-flex items-center gap-1.5 bg-[#0ECB81] hover:bg-[#0BB574] text-white text-xs font-semibold px-4 py-2 rounded-sm"
                  data-testid="profile-send-btn">
                  <Send className="w-3.5 h-3.5" /> Send crypto
                </Link>
                <Link to={`/signup?ref=${profile.referral_code}`}
                  className="inline-flex items-center gap-1.5 border border-[#2B3139] bg-[#181A20] hover:bg-[#2B3139] text-white text-xs px-4 py-2 rounded-sm"
                  data-testid="profile-refer-btn">
                  <Sparkles className="w-3.5 h-3.5 text-[#FCD535]" /> Use referral
                </Link>
              </div>
            )}
            {isMe && (
              <div className="text-xs text-[#0ECB81] mt-4 flex items-center gap-1">
                <User className="w-3 h-3" /> This is your profile
              </div>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3 mt-6">
          <div className="border border-[#2B3139] bg-[#0B0E11] p-4 rounded-sm">
            <div className="flex items-center gap-1.5 text-[10px] uppercase font-mono text-[#848E9C] mb-1">
              <TrendingUp className="w-3 h-3" /> Trades
            </div>
            <div className="text-white font-mono text-2xl" data-testid="profile-stat-trades">{profile.stats?.trades ?? 0}</div>
          </div>
          <div className="border border-[#2B3139] bg-[#0B0E11] p-4 rounded-sm">
            <div className="flex items-center gap-1.5 text-[10px] uppercase font-mono text-[#848E9C] mb-1">
              <UsersIcon className="w-3 h-3" /> Referrals
            </div>
            <div className="text-white font-mono text-2xl" data-testid="profile-stat-referrals">{profile.stats?.referrals ?? 0}</div>
          </div>
        </div>

        {isMe && (
          <div className="mt-6 border border-[#2B3139] bg-[#0B0E11] p-4 rounded-sm" data-testid="profile-own-username-setup">
            <div className="text-xs uppercase font-mono text-[#FCD535] mb-1">Your @username</div>
            <div className="text-[11px] text-[#B7BDC6] mb-3">
              {profile.username
                ? <>Friends can send you crypto by name: <span className="font-mono text-white">@{profile.username}</span></>
                : <>Pick a unique @handle so friends can send you crypto by name.</>}
            </div>
            <UsernameSetup compact />
          </div>
        )}
      </div>
    </div>
  );
}
